import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const NAV = [
  { label: "About", href: "#about" },
  { label: "For Companies", href: "#for-companies" },
  { label: "For Candidates", href: "#for-candidates" },
  { label: "Why Us", href: "#why-us" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-white/75 border-b border-brand-border/60 shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-24 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-brand">
          <img
            src="/logo.png"
            alt="Career Connect"
            className="h-16 w-16 sm:h-20 sm:w-20 object-contain"
            data-testid="nav-logo"
          />
          <div className="leading-tight text-center sm:text-left">
            <div className="font-heading text-xl sm:text-2xl font-semibold text-brand-ink">
              Career Connect
            </div>
            <div className="mt-0.5 text-[8px] sm:text-[9px] tracking-[0.08em] uppercase text-brand-muted font-semibold text-center">
              Right People at Right Place
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((item) => (
            <a
              key={item.href}
              href={item.href}
              data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-sm text-brand-muted hover:text-brand-ink transition-colors"
            >
              {item.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Button
            asChild
            variant="ghost"
            className="rounded-full text-brand-ink hover:bg-brand-surfaceAlt"
            data-testid="nav-secondary-cta"
          >
            <a href="#for-candidates">Career Guidance</a>
          </Button>
          <Button
            asChild
            className="rounded-full bg-brand-primary text-white hover:bg-brand-primaryHover px-5"
            data-testid="nav-primary-cta"
          >
            <a href="#contact">Hire Talent</a>
          </Button>
        </div>

        <button
          className="lg:hidden p-2 rounded-lg hover:bg-brand-surfaceAlt"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-brand-border/60 bg-white/95 backdrop-blur-xl">
          <div className="px-5 py-4 flex flex-col gap-3">
            {NAV.map((item) => (
              <a
                key={item.href}
                href={item.href}
                className="py-2 text-brand-ink"
                data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {item.label}
              </a>
            ))}
            <Button
              asChild
              className="rounded-full bg-brand-primary text-white hover:bg-brand-primaryHover mt-2"
              data-testid="mobile-nav-primary-cta"
            >
              <a href="#contact">Hire Talent</a>
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}
